import { Box, Flex, Image, Text } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import Button from 'components/Button/Button';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { mq } from 'styles/breakpoints';
import { rem } from 'utils/fonts';
import { getCountryWithCity } from 'utils/project';
import useBreakpoint from 'hooks/useBreakpoint';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { useSelector } from 'react-redux';
import IconButton from 'components/IconButton/IconButton';
import { ArrowRight } from 'components/Icons/Icons';

//region styled

const Item = styled(Box)`
  display: flex;
  align-items: flex-start;

  &:last-child {
    margin-bottom: 0;
  }

  ${mq.md} {
    align-items: center;
  }

  ${mq.lgMinMax} {
    align-items: flex-start;
  }
`;

const ProjectTitle = styled(Text)`
  display: flex;
  align-items: flex-start;
`;

const ButtonLabel = styled(Text)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ButtonLabelText = styled.span`
  display: inline-block;
  font-size: ${rem(15)};
`;

//endregion

interface Project {
  slug: string;
  status: string;
  title: string;
  country: string;
  city: string;
  imageUrl: string;
}

interface Props {
  project: Project;
  hideButton?: boolean;
}

const ProjectDetailsButton: FC<{ onClick?: () => void }> = ({ onClick }) => {
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const { t } = useTranslation('common');

  if (isCorporateTheme) {
    return (
      <IconButton variant="icon" ml="auto" mt={{ base: '15px', md: 0 }} aria-label={t('portfolio.view')} onClick={onClick} icon={<ArrowRight />} />
    );
  }

  return (
    <Button
      fontSize={rem(14)}
      fontWeight="semibold"
      variant="outline"
      colorScheme="gray"
      height="46px"
      p="11px 20px"
      w="160px"
      ml={['0px', '0px', 'auto', 'auto', '0px', 'auto']}
      mt={['15px', '15px', '0px', '0px', '15px', '0px']}
      onClick={onClick}
      minW="40%"
      d={['none', 'none', 'none', 'block']}
    >
      <ButtonLabel>
        <ButtonLabelText>{t('portfolio.view')}</ButtonLabelText>
        <ArrowForwardIcon boxSize="18px" />
      </ButtonLabel>
    </Button>
  );
};

const ProjectTile: FC<Props> = ({ project, hideButton }) => {
  const history = useHistory();
  const location = useLocation();

  const { isMobile, isSmallDesktop } = useBreakpoint();

  const isCorporateTheme = useSelector(isCorporateThemeSelector);

  const onProjectClick = (projectId: string): void => {
    history.push({ pathname: `/project/${projectId}`, state: { from: location.pathname } });
  };

  return (
    <Item as="li" cursor="pointer" onClick={(): void => onProjectClick(project.slug)} mb={isCorporateTheme ? '12px' : '30px'}>
      <Image borderRadius="base" boxSize="60px" objectFit="cover" src={`${project.imageUrl}?q=50&w=180&auto=format`} alt={project.title} />
      <Flex
        direction={isMobile || (isSmallDesktop && !isCorporateTheme) ? 'column' : 'row'}
        align={isMobile || (isSmallDesktop && !isCorporateTheme) ? 'flex-start' : 'center'}
        pl="23px"
        w="100%"
      >
        <Flex direction="column" mr="10px" maxW={{ base: '100%', md: hideButton ? '100%' : '60%' }}>
          <ProjectTitle layerStyle="projects.projectTileTitle" fontWeight={500}>
            {project.title}
          </ProjectTitle>
          <Text layerStyle="projects.projectTileLocation">{getCountryWithCity(project)}</Text>
        </Flex>

        {!hideButton && <ProjectDetailsButton />}
      </Flex>
    </Item>
  );
};

export default ProjectTile;
